import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowRight,
    Brain,
    CloudRain,
    GraduationCap,
    Settings,
    Target,
    Users,
} from "lucide-react";

const serviceOptions = [
    { value: "private-instruction", label: "Private Instruction" },
    { value: "semi-private-instruction", label: "Semi-Private Instruction" },
    { value: "small-group-instruction", label: "Small-Group Instruction" },
    { value: "private-group-instruction", label: "Private Group Instruction" },
    { value: "private-hitting-session", label: "Private Hitting Session" },
    { value: "virtual-instruction", label: "Virtual Instruction" },
    { value: "tennis-mindset-coaching", label: "Tennis Mindset Coaching" },
    { value: "parent-coaching", label: "Parent Coaching & Education" },
    { value: "other", label: "Not Sure / Other" },
];

export default function FrameworkServices() {
    const [locations, setLocations] = useState([]);
    const [busy, setBusy] = useState(false);

    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        service: "private-instruction",
        coach: "either",
        location: "",
        preferred_date: "",
        preferred_time: "",
        experience_level: "beginner",
        notes: "",
    });

    useEffect(() => {
        api.get("/locations")
            .then((response) => {
                setLocations(Array.isArray(response.data) ? response.data : []);
            })
            .catch(() => {
                setLocations([]);
            });
    }, []);

    const chooseService = (service) => {
        setForm((current) => ({
            ...current,
            service,
        }));

        window.setTimeout(() => {
            document
                .getElementById("book")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
    };

    const submit = async (event) => {
        event.preventDefault();
        setBusy(true);

        try {
            await api.post("/bookings", {
                brand: "framework",
                ...form,
            });

            toast.success(
                "Request received. We’ll contact you to confirm pricing, location, and availability."
            );

            setForm((current) => ({
                ...current,
                name: "",
                email: "",
                phone: "",
                preferred_date: "",
                preferred_time: "",
                notes: "",
            }));
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="framework-services-page">
            {/* HERO */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-14">
                <div className="fw-label mb-4">// Coaching & Performance Services</div>

                <h1 className="fw-display text-5xl sm:text-7xl">
                    Build your{" "}
                    <span className="text-[#a6ce39]">framework.</span>
                </h1>

                <p className="mt-6 text-white/65 max-w-3xl text-lg leading-relaxed">
                    From first-time players to experienced competitors, Framework
                    offers individualized tennis instruction, virtual development,
                    performance coaching, and parent education built around the
                    athlete.
                </p>

                <div className="mt-8 border border-[#a6ce39]/35 bg-[#a6ce39]/5 p-6 max-w-4xl">
                    <div className="fw-label text-[#a6ce39] mb-2">
                        Online Scheduling Coming Soon
                    </div>

                    <p className="text-white/70 leading-relaxed">
                        For now, please use the contact form on this page to ask
                        about pricing, training locations, scheduling options, and
                        coach availability.
                    </p>

                    <p className="mt-3 text-white/50 text-sm leading-relaxed">
                        Pricing ranges reflect variables such as coach travel,
                        training location, court or facility fees, coach
                        availability, lesson format, and scheduling efficiency.
                        Final pricing will always be confirmed before a session is
                        booked.
                    </p>

                    <button
                        type="button"
                        onClick={() => chooseService("private-instruction")}
                        className="fw-btn-primary mt-5"
                        data-testid="fw-services-top-inquiry"
                    >
                        Request Coaching
                        <ArrowRight size={18} strokeWidth={2.5} />
                    </button>
                </div>
            </section>

            <div className="fw-rule max-w-[1400px] mx-auto" />

            {/* ON-COURT SERVICES */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                <SectionHeading
                    icon={Target}
                    label="// On-Court Development"
                    title="Skill development."
                    accent="On court."
                    description="Instruction and training options designed around the athlete’s goals, playing level, and preferred learning environment."
                />

                <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <ServiceCard
                        title="Private Instruction"
                        price="$65–$100 per hour"
                        description="One-on-one coaching tailored to the athlete’s technical development, tactical awareness, playing level, and long-term goals."
                        buttonText="Request Private Instruction"
                        onClick={() => chooseService("private-instruction")}
                        testid="fw-private-instruction"
                    />

                    <ServiceCard
                        title="Semi-Private Instruction"
                        price="2 athletes · $40–$60 per player, per hour"
                        description="Shared instruction for two athletes who want focused coaching while still receiving individualized feedback."
                        buttonText="Request Semi-Private Instruction"
                        onClick={() => chooseService("semi-private-instruction")}
                        testid="fw-semi-private-instruction"
                    />

                    <ServiceCard
                        title="Small-Group Instruction"
                        price="3–4 athletes · $30–$45 per player, per hour"
                        description="Structured skill development combining technical instruction, movement, live-ball drills, and competitive situations."
                        buttonText="Request Group Instruction"
                        onClick={() => chooseService("small-group-instruction")}
                        testid="fw-small-group-instruction"
                    />

                    <ServiceCard
                        title="Private Group Instruction"
                        price="Starting at $120 per hour"
                        description="Reserve a customized lesson, clinic, or drill session for your family, friends, team, or organization."
                        buttonText="Request a Private Group"
                        onClick={() => chooseService("private-group-instruction")}
                        testid="fw-private-group-instruction"
                    />

                    <ServiceCard
                        title="Private Hitting Session"
                        price="$35–$75 per hour"
                        description="Live-ball hitting, movement, rhythm, rally repetitions, and point play without formal instruction."
                        buttonText="Request a Hitting Session"
                        onClick={() => chooseService("private-hitting-session")}
                        testid="fw-private-hitting-session"
                    />
                </div>
            </section>

            <div className="fw-rule max-w-[1400px] mx-auto" />

            {/* VIRTUAL */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                <SectionHeading
                    icon={CloudRain}
                    label="// Virtual Development"
                    title="Rain does not have to"
                    accent="stop your progress."
                    description="When weather, travel, or scheduling prevent on-court coaching, Framework coaches may be available virtually."
                />

                <div className="mt-10 grid lg:grid-cols-12 gap-6">
                    <article className="fw-card p-8 lg:col-span-7">
                        <div className="fw-label mb-3">Virtual Instruction</div>

                        <h3 className="fw-heading text-3xl">
                            $40–$60 per hour
                        </h3>

                        <p className="mt-2 fw-mono text-xs uppercase tracking-[0.2em] text-[#a6ce39]">
                            Based on coach availability
                        </p>

                        <p className="mt-6 text-white/65 leading-relaxed">
                            Cannot get onto the court because of weather? When
                            conditions do not permit on-court coaching, our coaches
                            are available virtually to help athletes continue
                            developing.
                        </p>

                        <p className="mt-4 text-white/65 leading-relaxed">
                            These sessions can be used for technical review,
                            tactical planning, tournament preparation, match
                            analysis, or maintaining momentum between on-court
                            lessons.
                        </p>

                        <button
                            type="button"
                            onClick={() => chooseService("virtual-instruction")}
                            className="fw-btn-primary mt-7"
                            data-testid="fw-virtual-instruction"
                        >
                            Request Virtual Coaching
                            <ArrowRight size={18} strokeWidth={2.5} />
                        </button>
                    </article>

                    <aside className="border border-white/10 bg-[#0f0f10] p-8 lg:col-span-5">
                        <div className="fw-label mb-4">
                            Virtual Sessions May Include
                        </div>

                        <ul className="space-y-3 text-white/65">
                            <ListItem>Virtual private instruction</ListItem>
                            <ListItem>Technical video review</ListItem>
                            <ListItem>Coach swing analysis</ListItem>
                            <ListItem>Match review</ListItem>
                            <ListItem>Tactical planning</ListItem>
                            <ListItem>Tournament preparation</ListItem>
                        </ul>

                        <div className="mt-7 border-l-2 border-[#a6ce39] pl-4">
                            <p className="text-white/75 text-sm leading-relaxed">
                                Virtual availability is limited. These sessions
                                often fill quickly when poor weather keeps players
                                off the court.
                            </p>
                        </div>
                    </aside>
                </div>
            </section>

            <div className="fw-rule max-w-[1400px] mx-auto" />

            {/* MINDSET */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                <SectionHeading
                    icon={Brain}
                    label="// Mental Performance"
                    title="Tennis mindset"
                    accent="coaching."
                    description="The technical game matters. So does the athlete’s ability to manage pressure, mistakes, emotion, and competition."
                />

                <div className="mt-10 grid lg:grid-cols-12 gap-6">
                    <article className="fw-card p-8 lg:col-span-7">
                        <div className="fw-label mb-3">
                            Tennis Mindset Coaching
                        </div>

                        <h3 className="fw-heading text-3xl">
                            $45–$75 per hour
                        </h3>

                        <p className="mt-6 text-white/65 leading-relaxed">
                            Individualized sessions focused on the mental and
                            behavioral skills that support stronger performance in
                            practice and competition.
                        </p>

                        <button
                            type="button"
                            onClick={() =>
                                chooseService("tennis-mindset-coaching")
                            }
                            className="fw-btn-outline mt-7"
                            data-testid="fw-mindset-coaching"
                        >
                            Request Mindset Coaching
                        </button>
                    </article>

                    <aside className="border border-white/10 bg-[#0f0f10] p-8 lg:col-span-5">
                        <div className="fw-label mb-4">
                            Sessions May Address
                        </div>

                        <ul className="grid sm:grid-cols-2 lg:grid-cols-1 gap-3 text-white/65">
                            <ListItem>Confidence</ListItem>
                            <ListItem>Focus</ListItem>
                            <ListItem>Emotional regulation</ListItem>
                            <ListItem>Pre-match routines</ListItem>
                            <ListItem>Between-point routines</ListItem>
                            <ListItem>Competition preparation</ListItem>
                            <ListItem>Responding to mistakes</ListItem>
                            <ListItem>Resilience and consistency</ListItem>
                            <ListItem>Decision-making under pressure</ListItem>
                        </ul>
                    </aside>
                </div>
            </section>

            <div className="fw-rule max-w-[1400px] mx-auto" />

            {/* PARENT EDUCATION */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                <SectionHeading
                    icon={GraduationCap}
                    label="// Parent Education"
                    title="Understand the game."
                    accent="Support the athlete."
                    description="You do not have to play tennis to become one of your child’s greatest sources of support."
                />

                <div className="mt-10">
                    <article className="fw-card p-8">
                        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
                            <div>
                                <div className="fw-label mb-3">
                                    Parent Coaching & Education
                                </div>

                                <h3 className="fw-heading text-3xl">
                                    Packages start at $30 per hour
                                </h3>
                            </div>

                            <button
                                type="button"
                                onClick={() => chooseService("parent-coaching")}
                                className="fw-btn-primary"
                                data-testid="fw-parent-coaching"
                            >
                                Request Parent Education
                                <ArrowRight size={18} strokeWidth={2.5} />
                            </button>
                        </div>

                        <p className="mt-6 text-white/65 leading-relaxed max-w-4xl">
                            These virtual sessions help parents understand tennis,
                            communicate more effectively, support healthy
                            development, and navigate the decisions that come with
                            recreational or competitive play.
                        </p>

                        <div className="mt-10 grid md:grid-cols-3 gap-4">
                            <TopicPanel title="New to Tennis?">
                                <ListItem>Rules and scoring</ListItem>
                                <ListItem>Tennis terminology</ListItem>
                                <ListItem>Etiquette and court culture</ListItem>
                                <ListItem>Lessons, leagues, and match formats</ListItem>
                                <ListItem>
                                    What to expect at practices and competitions
                                </ListItem>
                            </TopicPanel>

                            <TopicPanel title="Competitive Tennis">
                                <ListItem>USTA and UTR pathways</ListItem>
                                <ListItem>Tournament selection</ListItem>
                                <ListItem>Tournament weekend preparation</ListItem>
                                <ListItem>
                                    Training and competition schedules
                                </ListItem>
                                <ListItem>Communicating with coaches</ListItem>
                                <ListItem>
                                    Navigating higher-level competition
                                </ListItem>
                            </TopicPanel>

                            <TopicPanel title="Behavior & Family Support">
                                <ListItem>
                                    Difficult behavior before, during, or after play
                                </ListItem>
                                <ListItem>
                                    Emotional responses to mistakes and losses
                                </ListItem>
                                <ListItem>Motivation and resistance</ListItem>
                                <ListItem>Parent-athlete communication</ListItem>
                                <ListItem>Expectations and burnout</ListItem>
                                <ListItem>
                                    Independence, resilience, and routines
                                </ListItem>
                            </TopicPanel>
                        </div>
                    </article>
                </div>
            </section>

            <div className="fw-rule max-w-[1400px] mx-auto" />

            {/* THE LAB */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                <div className="grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-4">
                            <Settings
                                size={22}
                                className="text-[#a6ce39]"
                                strokeWidth={1.8}
                            />
                            <div className="fw-label">
                                // Stringing & Racquet Services
                            </div>
                        </div>

                        <h2 className="fw-display text-4xl sm:text-6xl">
                            Step into{" "}
                            <span className="text-[#a6ce39]">The Lab.</span>
                        </h2>

                        <p className="mt-6 text-white/65 text-lg leading-relaxed max-w-3xl">
                            Professional stringing, racquet customization,
                            accessories, setup guidance, and performance-focused
                            equipment services.
                        </p>

                        <p className="mt-3 text-white/50 leading-relaxed max-w-3xl">
                            Whether you need a fresh restring or a more customized
                            setup, The Lab is where equipment meets performance.
                        </p>
                    </div>

                    <div className="lg:col-span-5 lg:text-right">
                        <a
                            href="/framework/lab"
                            className="fw-btn-primary inline-flex"
                            data-testid="fw-step-into-lab"
                        >
                            Step Into The Lab
                            <ArrowRight size={18} strokeWidth={2.5} />
                        </a>
                    </div>
                </div>
            </section>

            <div className="fw-rule max-w-[1400px] mx-auto" />

            {/* PRICING */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                <div className="grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-5">
                        <div className="fw-label mb-3">
                            // Pricing & Availability
                        </div>

                        <h2 className="fw-display text-4xl">
                            Why prices{" "}
                            <span className="text-[#a6ce39]">vary.</span>
                        </h2>
                    </div>

                    <div className="lg:col-span-7">
                        <p className="text-white/65 leading-relaxed">
                            Framework coaching is organized around the athlete,
                            coach availability, and the logistics required to
                            deliver the session efficiently.
                        </p>

                        <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-white/60">
                            <ListItem>Training location</ListItem>
                            <ListItem>Coach travel</ListItem>
                            <ListItem>Court or facility fees</ListItem>
                            <ListItem>Coach availability</ListItem>
                            <ListItem>Lesson format</ListItem>
                            <ListItem>Number of athletes</ListItem>
                            <ListItem>Scheduling efficiency</ListItem>
                            <ListItem>Specialized service needs</ListItem>
                        </div>

                        <p className="mt-6 text-white/75 font-medium">
                            The applicable price will always be discussed and
                            confirmed before a lesson is finalized.
                        </p>
                    </div>
                </div>
            </section>

            <div className="fw-rule max-w-[1400px] mx-auto" />

            {/* CONTACT FORM */}
            <section
                id="book"
                className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 scroll-mt-20"
            >
                <div className="fw-label mb-3">// Coaching Inquiry</div>

                <h2 className="fw-display text-3xl sm:text-5xl mb-4">
                    Request a{" "}
                    <span className="text-[#a6ce39]">session.</span>
                </h2>

                <p className="text-white/60 max-w-3xl mb-8 leading-relaxed">
                    Online scheduling is coming soon. Until then, submit this form
                    to inquire about pricing, location, scheduling options, and
                    coach availability.
                </p>

                <form
                    onSubmit={submit}
                    className="grid md:grid-cols-2 gap-6 border border-white/10 p-6 sm:p-8 bg-[#0f0f10]"
                >
                    <FWF
                        label="Name"
                        required
                        value={form.name}
                        onChange={(value) =>
                            setForm({ ...form, name: value })
                        }
                        testid="fw-book-name"
                    />

                    <FWF
                        label="Email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(value) =>
                            setForm({ ...form, email: value })
                        }
                        testid="fw-book-email"
                    />

                    <FWF
                        label="Phone"
                        value={form.phone}
                        onChange={(value) =>
                            setForm({ ...form, phone: value })
                        }
                        testid="fw-book-phone"
                    />

                    <FWS
                        label="Service"
                        required
                        value={form.service}
                        onChange={(value) =>
                            setForm({ ...form, service: value })
                        }
                        testid="fw-book-service"
                        options={serviceOptions}
                    />

                    <FWS
                        label="Coach"
                        value={form.coach}
                        onChange={(value) =>
                            setForm({ ...form, coach: value })
                        }
                        testid="fw-book-coach"
                        options={[
                            {
                                value: "either",
                                label: "No preference",
                            },
                            {
                                value: "Jacob Eastwood",
                                label: "Jacob Eastwood",
                            },
                            {
                                value: "Aubrey Biddle",
                                label: "Aubrey Biddle",
                            },
                        ]}
                    />

                    <FWS
                        label="Preferred Area or Location"
                        value={form.location}
                        onChange={(value) =>
                            setForm({ ...form, location: value })
                        }
                        testid="fw-book-location"
                        options={[
                            {
                                value: "",
                                label: "Open to recommendation",
                            },
                            ...locations.map((location) => ({
                                value: location.name,
                                label: location.type
                                    ? `${location.name} (${location.type})`
                                    : location.name,
                            })),
                        ]}
                    />

                    <FWF
                        label="Preferred Date"
                        type="date"
                        value={form.preferred_date}
                        onChange={(value) =>
                            setForm({
                                ...form,
                                preferred_date: value,
                            })
                        }
                        testid="fw-book-date"
                    />

                    <FWF
                        label="Preferred Time"
                        type="time"
                        value={form.preferred_time}
                        onChange={(value) =>
                            setForm({
                                ...form,
                                preferred_time: value,
                            })
                        }
                        testid="fw-book-time"
                    />

                    <FWS
                        label="Experience Level"
                        value={form.experience_level}
                        onChange={(value) =>
                            setForm({
                                ...form,
                                experience_level: value,
                            })
                        }
                        testid="fw-book-level"
                        options={[
                            {
                                value: "beginner",
                                label: "Beginner / New Player",
                            },
                            {
                                value: "intermediate",
                                label: "Intermediate",
                            },
                            {
                                value: "advanced",
                                label: "Advanced / Competitive",
                            },
                            {
                                value: "junior",
                                label: "Junior Player",
                            },
                            {
                                value: "not-sure",
                                label: "Not Sure",
                            },
                        ]}
                    />

                    <div className="md:col-span-2">
                        <label className="fw-label block mb-2">
                            Tell Us What You Need
                        </label>

                        <textarea
                            rows={5}
                            className="fw-input min-h-[120px]"
                            style={{
                                borderBottom:
                                    "1px solid rgba(255,255,255,0.22)",
                            }}
                            value={form.notes}
                            onChange={(event) =>
                                setForm({
                                    ...form,
                                    notes: event.target.value,
                                })
                            }
                            placeholder="Tell us about the athlete, preferred area, goals, availability, or any questions you have."
                            data-testid="fw-book-notes"
                        />
                    </div>

                    <div className="md:col-span-2">
                        <button
                            type="submit"
                            disabled={busy}
                            className="fw-btn-primary"
                            data-testid="fw-book-submit"
                        >
                            {busy ? "Sending…" : "Send Inquiry"}
                            <ArrowRight size={18} strokeWidth={2.5} />
                        </button>
                    </div>
                </form>
            </section>

            <div className="fw-rule max-w-[1400px] mx-auto" />

            {/* CANCELLATION POLICY */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                <div className="border border-white/10 bg-[#0f0f10] p-6 sm:p-8 lg:p-10">
                    <div className="fw-label mb-3">
                        // Lesson Cancellation Policy
                    </div>

                    <h2 className="fw-heading text-3xl">
                        Reserved specifically for you.
                    </h2>

                    <p className="mt-5 text-white/65 leading-relaxed max-w-5xl">
                        At Framework, every lesson time is reserved specifically
                        for you. Because our coaches often travel between training
                        locations and carefully coordinate schedules throughout
                        the day, last-minute cancellations can create significant
                        gaps that cannot be filled on short notice.
                    </p>

                    <p className="mt-4 text-white/65 leading-relaxed max-w-5xl">
                        We understand that unexpected situations and emergencies
                        happen. If you need to cancel or reschedule a lesson,
                        please communicate with us as soon as possible. Prompt
                        communication gives us the best opportunity to adjust our
                        schedule and, when possible, offer that lesson time to
                        another athlete.
                    </p>

                    <div className="mt-8 grid md:grid-cols-2 gap-4">
                        <div className="border border-white/10 p-6">
                            <div className="fw-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
                                Within 24 Hours
                            </div>

                            <div className="fw-heading text-2xl text-[#a6ce39] mt-2">
                                50% of the lesson fee
                            </div>

                            <p className="mt-3 text-white/55 text-sm leading-relaxed">
                                Cancellations made within 24 hours of the scheduled
                                lesson are subject to a 50% cancellation fee.
                            </p>
                        </div>

                        <div className="border border-white/10 p-6">
                            <div className="fw-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
                                Within 12 Hours
                            </div>

                            <div className="fw-heading text-2xl text-[#a6ce39] mt-2">
                                Full lesson fee
                            </div>

                            <p className="mt-3 text-white/55 text-sm leading-relaxed">
                                Cancellations made within 12 hours of the scheduled
                                lesson are subject to 100% of the lesson fee.
                            </p>
                        </div>
                    </div>

                    <div className="mt-8 border-l-2 border-[#a6ce39] pl-5">
                        <p className="text-white/65 leading-relaxed">
                            Lessons canceled by Framework due to unsafe weather or
                            unplayable court conditions will not incur a
                            cancellation fee. Whenever possible, we will work with
                            you to reschedule or offer an appropriate virtual
                            coaching option.
                        </p>
                    </div>

                    <p className="mt-6 text-white/75">
                        Thank you for helping us make the most of every available
                        court time.
                    </p>
                </div>
            </section>
        </div>
    );
}

function SectionHeading({
    icon: Icon,
    label,
    title,
    accent,
    description,
}) {
    return (
        <div className="grid lg:grid-cols-12 gap-6 items-end">
            <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-4">
                    <Icon
                        size={22}
                        className="text-[#a6ce39]"
                        strokeWidth={1.8}
                    />
                    <div className="fw-label">{label}</div>
                </div>

                <h2 className="fw-display text-4xl sm:text-6xl">
                    {title}{" "}
                    <span className="text-[#a6ce39]">{accent}</span>
                </h2>
            </div>

            <p className="lg:col-span-5 text-white/60 leading-relaxed">
                {description}
            </p>
        </div>
    );
}

function ServiceCard({
    title,
    price,
    description,
    buttonText,
    onClick,
    testid,
}) {
    return (
        <article
            className="fw-card p-7 flex flex-col"
            data-testid={`${testid}-card`}
        >
            <div className="fw-label mb-3">On-Court Service</div>

            <h3 className="fw-heading text-2xl">{title}</h3>

            <p className="mt-3 fw-mono text-xs uppercase tracking-[0.15em] text-[#a6ce39]">
                {price}
            </p>

            <p className="mt-5 text-white/60 text-sm leading-relaxed flex-1">
                {description}
            </p>

            <button
                type="button"
                onClick={onClick}
                className="fw-btn-outline mt-6 text-xs"
                data-testid={testid}
            >
                {buttonText}
            </button>
        </article>
    );
}

function TopicPanel({ title, children }) {
    return (
        <div className="border border-white/10 bg-black/20 p-6">
            <div className="flex items-center gap-2 mb-4">
                <Users
                    size={17}
                    className="text-[#a6ce39]"
                    strokeWidth={1.8}
                />
                <h4 className="fw-heading text-xl">{title}</h4>
            </div>

            <ul className="space-y-3 text-white/60 text-sm">{children}</ul>
        </div>
    );
}

function ListItem({ children }) {
    return (
        <li className="flex gap-3 leading-relaxed">
            <span className="text-[#a6ce39] mt-[1px]">—</span>
            <span>{children}</span>
        </li>
    );
}

function FWF({
    label,
    testid,
    value,
    onChange,
    type = "text",
    required,
}) {
    return (
        <label className="block">
            <div className="fw-label mb-2">
                {label}
                {required && (
                    <span className="text-[#a6ce39]"> *</span>
                )}
            </div>

            <input
                type={type}
                value={value}
                required={required}
                onChange={(event) => onChange(event.target.value)}
                data-testid={testid}
                className="fw-input"
            />
        </label>
    );
}

function FWS({
    label,
    testid,
    value,
    onChange,
    options,
    required,
}) {
    return (
        <label className="block">
            <div className="fw-label mb-2">
                {label}
                {required && (
                    <span className="text-[#a6ce39]"> *</span>
                )}
            </div>

            <select
                value={value}
                required={required}
                onChange={(event) => onChange(event.target.value)}
                data-testid={testid}
                className="fw-select"
            >
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </label>
    );
}